import useUrlState, {
  type UrlVal,
  type UrlValOption,
  type SetParam,
  useBatchUrlState,
  useDeleteQueryParams,
  prepareObjectForQuery,
} from "./useUrlState";

// Re-export types and utilities so consumers never need to import from useUrlState directly
export type { UrlVal, UrlValOption, SetParam };
export { useBatchUrlState, useDeleteQueryParams, prepareObjectForQuery };

// Wrappers are typed aliases over useUrlState. They centralize the URL-param
// key strings and let ESLint forbid direct useUrlState imports outside this
// file. Wrappers accept an optional initialValue: passing it makes useUrlState
// seed the URL on mount (via useInitialQueryParam) and return that value
// when the URL is empty. Omitting it leaves the URL untouched and returns
// undefined when the URL has no value — preferred when downstream code uses
// `typeof x === "undefined"` to detect "user hasn't set this yet".

// Generic hook for dynamic key use cases (e.g. usePreselectOptions)
export function useDynamicUrlState<K extends keyof UrlVal>(
  key: K,
  method?: "push" | "replace"
) {
  return useUrlState(key, undefined, method);
}

// ---------------------------------------------------------------------------
// Replace Proxy Page
// ---------------------------------------------------------------------------

export function useAddTypeUrlState(method?: "push" | "replace") {
  return useUrlState("addType", undefined, method);
}

export function useRemoveTypeUrlState(
  method?: "push" | "replace",
  initialValue?: UrlVal["removeType"]
) {
  return useUrlState("removeType", initialValue, method);
}

export function useReplaceProxyStepUrlState(method?: "push" | "replace") {
  return useUrlState("replaceProxyStep", undefined, method);
}

export function useRemoveAsnUrlState(method?: "push" | "replace") {
  return useUrlState("removeAsn", undefined, method);
}

export function useRemoveCountryUrlState(method?: "push" | "replace") {
  return useUrlState("removeCountry", undefined, method);
}

export function useRemoveRangeUrlState(method?: "push" | "replace") {
  return useUrlState("removeRange", undefined, method);
}

export function useVerificationCategoryUrlState(method?: "push" | "replace") {
  return useUrlState("verificationCategory", undefined, method);
}

export function useRemoveIpsTextUrlState(method?: "push" | "replace") {
  return useUrlState("removeIpsText", undefined, method);
}

export function useAddAsnsUrlState(method?: "push" | "replace") {
  return useUrlState("addAsns", undefined, method);
}

export function useAddCountriesUrlState(method?: "push" | "replace") {
  return useUrlState("addCountries", undefined, method);
}

export function useAddRangesUrlState(method?: "push" | "replace") {
  return useUrlState("addRanges", undefined, method);
}

// ---------------------------------------------------------------------------
// Activity Page
// ---------------------------------------------------------------------------

export function useActivityTimestampLteUrlState(method?: "push" | "replace") {
  return useUrlState("activityTimestampLte", undefined, method);
}

export function useActivityTimestampGteUrlState(method?: "push" | "replace") {
  return useUrlState("activityTimestampGte", undefined, method);
}

// ---------------------------------------------------------------------------
// List Page
// ---------------------------------------------------------------------------

export function useAuthenticationMethodUrlState(method?: "push" | "replace") {
  return useUrlState("authenticationMethod", undefined, method);
}

export function useProxyControlUrlState(method?: "push" | "replace") {
  return useUrlState("proxyControl", undefined, method);
}

export function useFilterByCountryOpenUrlState(method?: "push" | "replace") {
  return useUrlState("filterByCountryOpen", undefined, method);
}

export function useExampleCodeOpenUrlState(method?: "push" | "replace") {
  return useUrlState("exampleCodeOpen", undefined, method);
}

export function useShowQuickStartUrlState(method?: "push" | "replace") {
  return useUrlState("showQuickStart", undefined, method);
}

export function usePlanIdUrlState(method?: "push" | "replace") {
  return useUrlState("planId", undefined, method);
}

// ---------------------------------------------------------------------------
// Customize
// ---------------------------------------------------------------------------

export function useIsCustomPlanUrlState(method?: "push" | "replace") {
  return useUrlState("isCustomPlan", undefined, method);
}

export function useCustomPlanTypeUrlState(method?: "push" | "replace") {
  return useUrlState("customPlanType", undefined, method);
}

export function useProxyTypeUrlState(
  method?: "push" | "replace",
  initialValue?: UrlVal["proxyType"]
) {
  return useUrlState("proxyType", initialValue, method);
}

export function useProxySubtypeUrlState(
  method?: "push" | "replace",
  initialValue?: UrlVal["proxySubtype"]
) {
  return useUrlState("proxySubtype", initialValue, method);
}

export function useProxyCountUrlState(method?: "push" | "replace") {
  return useUrlState("proxyCount", undefined, method);
}

export function useCustomProxyCountUrlState(method?: "push" | "replace") {
  return useUrlState("customProxyCount", undefined, method);
}

export function useAutoRefreshFrequencyUrlState(method?: "push" | "replace") {
  return useUrlState("autoRefreshFrequency", undefined, method);
}

export function useIsCustomAutoRefreshFrequencyUrlState(
  method?: "push" | "replace"
) {
  return useUrlState("isCustomAutoRefreshFrequency", undefined, method);
}

export function useIsUnlimitedIpAuthorizationsUrlState(
  method?: "push" | "replace"
) {
  return useUrlState("isUnlimitedIpAuthorizations", undefined, method);
}

export function useIsHighConcurrencyUrlState(method?: "push" | "replace") {
  return useUrlState("isHighConcurrency", undefined, method);
}

export function useIsHighPriorityNetworkUrlState(method?: "push" | "replace") {
  return useUrlState("isHighPriorityNetwork", undefined, method);
}

export function useIsHighIpReputationUrlState(
  method?: "push" | "replace",
  initialValue?: UrlVal["isHighIpReputation"]
) {
  return useUrlState("isHighIpReputation", initialValue, method);
}

export function useIsYearlyUrlState(method?: "push" | "replace") {
  return useUrlState("isYearly", undefined, method);
}

export function useIs2xProxiesUrlState(method?: "push" | "replace") {
  return useUrlState("is2xProxies", undefined, method);
}

export function useAddonPlanUrlState(method?: "push" | "replace") {
  return useUrlState("addonPlan", undefined, method);
}

export function useIsCustomSubuserCountUrlState(method?: "push" | "replace") {
  return useUrlState("isCustomSubuserCount", undefined, method);
}

export function useIsCustomOnDemandRefreshesUrlState(
  method?: "push" | "replace"
) {
  return useUrlState("isCustomOnDemandRefreshes", undefined, method);
}

export function useProxyReplacementsUrlState(method?: "push" | "replace") {
  return useUrlState("proxyReplacements", undefined, method);
}

export function useIsCustomProxyReplacementsUrlState(
  method?: "push" | "replace"
) {
  return useUrlState("isCustomProxyReplacements", undefined, method);
}

/** Whether the Rotating Residential bandwidth is a custom amount (R-1924). */
export function useIsCustomBandwidthUrlState(method?: "push" | "replace") {
  return useUrlState("isCustomBandwidth", undefined, method);
}

export function useOnDemandRefreshesUrlState(method?: "push" | "replace") {
  return useUrlState("onDemandRefreshes", undefined, method);
}

export function useSubuserCountUrlState(method?: "push" | "replace") {
  return useUrlState("subuserCount", undefined, method);
}

export function useCustomSubuserCountUrlState(method?: "push" | "replace") {
  return useUrlState("customSubuserCount", undefined, method);
}

export function useBandwidthUrlState(method?: "push" | "replace") {
  return useUrlState("bandwidth", undefined, method);
}

export function useSiteChecksUrlState(method?: "push" | "replace") {
  return useUrlState("siteChecks", undefined, method);
}

// ---------------------------------------------------------------------------
// Table / Pagination
// ---------------------------------------------------------------------------

export function useRowsPerPageUrlState(method?: "push" | "replace") {
  return useUrlState("rowsPerPage", undefined, method);
}

export function usePageUrlState(method?: "push" | "replace") {
  return useUrlState("page", undefined, method);
}

export function useOrderUrlState(method?: "push" | "replace") {
  return useUrlState("order", undefined, method);
}

export function useOrderByUrlState(method?: "push" | "replace") {
  return useUrlState("orderBy", undefined, method);
}

export function useSearchValueUrlState(method?: "push" | "replace") {
  return useUrlState("searchValue", undefined, method);
}

export function useFiltersUrlState(method?: "push" | "replace") {
  return useUrlState("filters", undefined, method);
}

// ---------------------------------------------------------------------------
// Customize (continued)
// ---------------------------------------------------------------------------

export function useCustomAutomaticRefreshFrequencyPeriodUrlState(
  method?: "push" | "replace"
) {
  return useUrlState(
    "customAutomaticRefreshFrequencyPeriod",
    undefined,
    method
  );
}

export function useCountriesUrlState(method?: "push" | "replace") {
  return useUrlState("countries", undefined, method);
}

// ---------------------------------------------------------------------------
// Express Checkout
// ---------------------------------------------------------------------------

export function useShowPlanDetailsUrlState(method?: "push" | "replace") {
  return useUrlState("showPlanDetails", undefined, method);
}

export function useSourceUrlState(method?: "push" | "replace") {
  return useUrlState("source", undefined, method);
}

// ---------------------------------------------------------------------------
// Subscription Page
// ---------------------------------------------------------------------------

export function useOpenPaymentMethodUrlState(method?: "push" | "replace") {
  return useUrlState("openPaymentMethod", undefined, method);
}

// ---------------------------------------------------------------------------
// Cancel Subscription Page
// ---------------------------------------------------------------------------

export function usePlanIdsUrlState(method?: "push" | "replace") {
  return useUrlState("planIds", undefined, method);
}

// ---------------------------------------------------------------------------
// Endpoint Generator
// ---------------------------------------------------------------------------

export function useEndpointAuthMethodUrlState(method?: "push" | "replace") {
  return useUrlState("endpointAuthMethod", undefined, method);
}

export function useEndpointAuthorizedIpUrlState(method?: "push" | "replace") {
  return useUrlState("endpointAuthorizedIp", undefined, method);
}

export function useEndpointCountriesUrlState(method?: "push" | "replace") {
  return useUrlState("endpointCountries", undefined, method);
}

export function useEndpointCityUrlState(method?: "push" | "replace") {
  return useUrlState("endpointCity", undefined, method);
}

export function useEndpointUsStateUrlState(method?: "push" | "replace") {
  return useUrlState("endpointState", undefined, method);
}

export function useEndpointZipUrlState(method?: "push" | "replace") {
  return useUrlState("endpointZip", undefined, method);
}

export function useEndpointAsnUrlState(method?: "push" | "replace") {
  return useUrlState("endpointAsn", undefined, method);
}

export function useEndpointSessionTypeUrlState(method?: "push" | "replace") {
  return useUrlState("endpointSessionType", undefined, method);
}
